export const IDENTITY_VALIDATION_STATUS = {
  ACTIVE: 1,
  WAITING_KYC: 6,
  WAITING_MANUAL_KYC: 7,
  WAITING_MANUAL_REVIEW: 8,
  REJECTED: 9,
};

export const IDENTITY_VALIDATION_DECISION = {
  APPROVED: 'APPROVED',
  RETRY: 'RETRY',
  MANUAL: 'MANUAL',
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
  UNKNOWN: 'UNKNOWN',
};

const BLOCKED_STATUSES = new Set([
  IDENTITY_VALIDATION_STATUS.WAITING_KYC,
  IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_KYC,
  IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_REVIEW,
  IDENTITY_VALIDATION_STATUS.REJECTED,
]);

function normalizeStatus(value) {
  if (value === null || value === undefined || value === '') return null;
  const status = Number(value);
  return Number.isNaN(status) ? null : status;
}

export function mapUserStatusToIdentityValidation(userStatusResponse) {
  const payload = userStatusResponse?.data ?? userStatusResponse ?? {};

  return mapIdentityValidationState({
    id_user_status: payload.id_user_status,
  });
}

export function mapIdentityValidationState(routeSummary) {
  const source = routeSummary?.identity_validation ?? routeSummary?.kyc ?? {};
  const status = normalizeStatus(
    source.id_user_status ?? source.user_status ?? routeSummary?.id_user_status
  );
  const enabled =
    routeSummary?.is_identity_validation_disabled === true
      ? false
      : source.enabled ??
        source.is_enabled ??
        routeSummary?.is_identity_validation_enabled ??
        status !== null;

  return {
    enabled: Boolean(enabled),
    status,
    isRouteAssignmentBlocked: Boolean(enabled) && BLOCKED_STATUSES.has(status),
    requiresManualValidation:
      status === IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_KYC,
    isPendingManualReview:
      status === IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_REVIEW,
    isIdentityRejected: status === IDENTITY_VALIDATION_STATUS.REJECTED,
  };
}

export function resolveSubmittedIdentityDecision(result = {}, method) {
  const decision = resolveIdentityValidationDecision(result);
  if (
    method === 'submitManual' &&
    decision !== IDENTITY_VALIDATION_DECISION.APPROVED &&
    decision !== IDENTITY_VALIDATION_DECISION.REJECTED &&
    decision !== IDENTITY_VALIDATION_DECISION.PENDING
  ) {
    return IDENTITY_VALIDATION_DECISION.PENDING;
  }
  return decision;
}

function readErrorText(error) {
  const body = error?.data?.data ?? error?.response?.data;
  return [body?.message, body?.data, body, error?.message]
    .filter((value) => typeof value === 'string')
    .join(' ')
    .toUpperCase();
}

export function isRepeatedManualValidationError(error) {
  const status = error?.data?.status ?? error?.response?.status;
  return status === 400 && readErrorText(error).includes('INVALID_ACTION');
}

export function resolveIdentityValidationDecision(result = {}) {
  const status = normalizeStatus(result.id_user_status ?? result.status);
  const retry = typeof result.retry === 'boolean' ? result.retry : null;

  if (status === IDENTITY_VALIDATION_STATUS.ACTIVE) {
    return IDENTITY_VALIDATION_DECISION.APPROVED;
  }
  if (status === IDENTITY_VALIDATION_STATUS.REJECTED) {
    return IDENTITY_VALIDATION_DECISION.REJECTED;
  }
  if (status === IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_REVIEW) {
    return IDENTITY_VALIDATION_DECISION.PENDING;
  }
  if (
    status === IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_KYC ||
    retry === false
  ) {
    return IDENTITY_VALIDATION_DECISION.MANUAL;
  }
  if (status === IDENTITY_VALIDATION_STATUS.WAITING_KYC && retry === true) {
    return IDENTITY_VALIDATION_DECISION.RETRY;
  }
  return IDENTITY_VALIDATION_DECISION.UNKNOWN;
}

export function normalizeIdentityValidationResult(response) {
  const data = response?.data ?? response ?? {};
  return {
    id_user_status: normalizeStatus(data.id_user_status ?? data.status),
    id_user_event: data.id_user_event ?? data.id ?? null,
    retry: typeof data.retry === 'boolean' ? data.retry : null,
  };
}
